<template>
  <loading-page
    v-if="initialLoading"
    full
  />
  <div
    v-else
    class="table-wrapper healthchecks"
  >
    <div class="d-block d-sm-flex">
      <b-button
        variant="icon-light"
        class="shadow"
        @click="$router.push({ name: 'healthchecks' })"
      >
        <GjIcon name="ArrowLeft" />
        Back
      </b-button>
      <div class="d-flex mt-1 mt-sm-0 w-100">
        <b-button
          v-if="$can('create', 'healthchecks', 'snitch')"
          variant="bg-primary"
          class="icon-left"
          name="Plus"
          @click="onCreateGroup()"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create Healthcheck Group
        </b-button>
        <button-loading
          variant="bg-white"
          class="ml-auto mr-0"
          :spinning="loading"
          :disabled="loading"
          @click="fetchGroups()"
        >
          <GjIcon
            class="icon-left"
            name="Refresh"
          />
          Refresh
        </button-loading>
      </div>
    </div>
    <div
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <!-- DESKTOP -->
      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="table"
          :items="items"
          :fields="fields"
          select-mode="single"
          show-empty
          responsive
          :class="{ loaded: !loading || removingGroup }"
          :busy="loading"
        >
          <template #cell(id)="data">
            <div
              class="favorite-wrapper"
            >
              <a
                v-b-popover.hover.focus="'Set group as favorite to show on top of the rest when selecting a group.'"
                class="favorite"
                @click="updateFavoriteGroup(data.item)"
              >
                <GjIcon
                  :key="`favorite${data.item.isDefault}`"
                  :name="data.item.isDefault ? 'Star_fill' : 'Star'"
                />
              </a>
            </div>
          </template>
          <template #cell(description)="data">
            <div class="d-flex align-items-center">
              <span class="text-truncate mr-1">{{ data.value }}</span>
              <a
                v-if="$can('update', 'healthchecks', 'snitch')"
                class="ml-auto mr-1"
                @click="onEditClick(data.item.id)"
              >
                <GjIcon
                  name="Edit"
                  size="20"
                />
              </a>
              <a
                v-if="$can('delete', 'healthchecks', 'snitch')"
                @click="onDeleteClick(data.item)"
              >
                <GjIcon
                  class="icon-left"
                  name="Delete"
                  size="20"
                />
              </a>
            </div>
          </template>

          <template #empty>
            <div
              v-if="loading"
              class="loading-table text-center py-1"
            >
              <b-spinner variant="primary" />
            </div>
            <template v-else>
              <no-data
                no-data-text="No healthcheck groups have been added yet..."
                create-text="+ Create Healthcheck Group"
                access-page="Healthcheck Groups"
                icon="Monitoring"
                :has-permissions="hasHealthcheckPermissions"
                @action="onCreateHealthcheck()"
              />
            </template>
          </template>
        </b-table>
        <table-busy v-if="loading && items.length > 0" />
      </div>
      <!-- MOBILE -->
      <div class="position-relative d-lg-none mt-2">
        <div :class="{ 'opacity-7': loading || removingGroup }">
          <template v-for="group in items">
            <card
              :key="group.id"
              :fields="fields"
              :item="group"
              class="top-fade-in"
              :show-arrow="false"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item
                    v-if="$can('update', 'healthchecks', 'snitch')"
                    @click="onEditClick(group.id)"
                  >
                    Edit
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('delete', 'healthchecks', 'snitch')"
                    @click="onDeleteClick(group)"
                  >Delete</b-dropdown-item>
                </b-dropdown>
              </template>
              <template #name>
                {{ group.name }}
                <div class="favorite-wrapper show icon-right">
                  <a
                    v-b-popover.hover.focus="'Set group as favorite to show on top of the rest when selecting a group.'"
                    class="favorite"
                    @click="updateFavoriteGroup(group)"
                  >
                    <GjIcon
                      :key="`favorite${group.isDefault}`"
                      :name="group.isDefault ? 'Star_fill' : 'Star'"
                    />
                  </a>
                </div>
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="loading || removingGroup"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && items.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No healthcheck groups have been added yet..."
              create-text="+ Create Healthcheck"
              access-page="Healthchecks"
              icon="Monitoring"
              :has-permissions="hasHealthcheckPermissions"
              @action="onCreateHealthcheck()"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="items.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>

    <group-form
      v-if="showForm"
      :group-id="groupId"
      @groupAdded="updateGroups(true)"
      @close="updateGroups"
    />

    <b-modal
      id="delete-group"
      title="Delete Healthcheck Group"
      cancel-variant="bg-white"
      ok-title="Delete"
      ok-variant="danger"
      header-class="text-break"
      centered
      @ok.prevent="deleteGroup"
      @hidden="() => {
        group = null
        deleteHealthchecks = false
      }"
    >
      <p>Are you sure you want to delete healthcheck group{{ group ? `: ${group.name}` : '' }}?</p>
      <b-form-checkbox v-model="deleteHealthchecks">
        Delete all healthchecks associated with this group
      </b-form-checkbox>
      <custom-alert
        icon-name="InfoAlt"
        items-center
        variant="light"
        class="mb-0 mt-1"
      >
        If left uncheck all healthchecks associated with this group will become uncategorized
      </custom-alert>
    </b-modal>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'
import GroupForm from './helpers/Form.vue'

export default {
  name: 'HealthcheckGroupList',
  components: { Card, GroupForm },
  data() {
    return {
      initialLoading: true,
      hasHealthcheckPermissions: true,
      fields: [
        {
          key: 'id',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'name', isPrimary: true, variant: 'default-col' },
        { key: 'description', isPrimary: true, variant: 'fill' },
      ],
      showForm: false,
      groupId: null,
      group: null,
      deleteHealthchecks: false,
    }
  },
  computed: {
    removingGroup() {
      return this.$store.state.healthchecks.removingHealthcheckGroup
    },
    loading() {
      return this.$store.state.healthchecks.fetchingHealthcheckGroups
    },
    items() {
      return this.$store.state.healthchecks.healthcheckGroups
    },
    totalCount() {
      return this.resultInfo?.totalCount
    },
    resultInfo() {
      return this.$store.state.healthchecks.healthcheckGroupsResultInfo
    },
    pageSize() {
      return this.resultInfo?.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentPage: {
      get() {
        return this.$store.getters['healthchecks/groupsCurrentPage']
      },
      set(value) {
        this.$store.commit('healthchecks/SET_GROUPS_CURRENT_PAGE', value)
      },
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
  },
  watch: {
    pageIndex() {
      this.fetchGroups()
    },
  },
  created() {
    this.fetchGroups()
  },
  methods: {
    fetchGroups() {
      this.$store
        .dispatch('healthchecks/fetchHealthcheckGroups', {
          pageIndex: this.currentPage - 1,
          pageSize: this.pageSize,
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 426 || status === 428) {
            this.$store.commit('observability/SET_SERVICE_UNAVAILABLE', { serviceUnavailable: true, serviceRequested: status === 428 })
            return
          }
          if (status === 403) {
            this.hasHealthcheckPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching healthcheck groups!',
          )
        })
        .finally(() => { this.initialLoading = false })
    },
    onCreateGroup() {
      this.showForm = true
    },
    onEditClick(id) {
      this.groupId = id
      this.showForm = true
    },
    onDeleteClick(group) {
      this.group = group
      this.$bvModal.show('delete-group')
    },
    deleteGroup() {
      this.$store
        .dispatch('healthchecks/removeHealthcheckGroup', {
          id: this.group.id,
          deleteHealthchecks: this.deleteHealthchecks,
        })
        .then(() => {
          this.successToast('Healthcheck group was removed')
          if (this.items.length === 0 && this.currentPage > 1) {
            this.fetchGroups = 1
          } else {
            this.fetchGroups()
          }
          this.$bvModal.hide('delete-group')
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting healthcheck group.')
        })
    },
    updateGroups(getGroups) {
      this.showForm = false
      this.groupId = null
      if (getGroups) this.fetchGroups()
    },
    updateFavoriteGroup(item) {
      this.$store
        .dispatch('healthchecks/updateHealthcheckGroup', {
          id: item.id,
          group: {
            name: item.name,
            description: item.description,
            isDefault: !item.isDefault,
          },
        })
        .catch(error => {
          this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while setting favorite healthcheck group.')
          this.fetchGroups()
        })
      this.$store.commit('healthchecks/SET_FAVORITE_GROUP', item.id)
    },
  },
}
</script>
<style lang="scss" scoped>
.favorite-wrapper {
  .favorite {
    color: var(--yellow);
  }
}
</style>
